import { collection, doc, getDoc, setDoc } from "firebase/firestore";
import Failure from "../../../core/Failure";
import { FirestoreCollections, firestore } from "../../../firebase.config";
import { IAdmin } from "@leedpulse-monorepo/interfaces";
import { UserRole } from '@leedpulse-monorepo/enums';
import { invalidOutboundPricingCountries, outboundPricingSignature, outboundPricingVersion } from '@leedpulse-monorepo/utils';
import getOutboundPricingHistory, { ADMIN_HISTORY_SUBCOLLECTION, OUTBOUND_PRICING_HISTORY_FIELD } from './getOutboundPricingHistory';
import { Authentication } from '@leedpulse-monorepo/classes';

/**
 * `loaded` is the doc as the panel opened it: the whole doc is rewritten, so a grid saved by
 * someone else since then would be erased silently. It is refused instead, naming who saved it.
 */
async function updateAdmin(
  admin: IAdmin,
  auth: Authentication,
  loaded?: IAdmin
): Promise<void> {
  if (![UserRole.owner, UserRole.admin].includes(auth.role)) {
    throw new UpdateAdminFailure("Unauthorized", "unauthorized");
  }

  // A country sold out of bundle needs its two prices: they are what the backend bills
  const aInvalidCountries = invalidOutboundPricingCountries(admin.telephony?.call_outbound_pricing);
  if (aInvalidCountries.length > 0) {
    throw new UpdateAdminFailure(`Tarif fixe ou mobile manquant, nul ou à plus de 3 décimales pour : ${aInvalidCountries.join(', ')}`, 'invalid-outbound-pricing');
  }

  try {

    if (admin.id === '') {
      throw new UpdateAdminFailure('Les informations renseignées sont incomplètes');
    }

    const data = Object.fromEntries(
      Object.entries(admin).filter(([, value]) => value !== undefined)
    );

    const db = firestore;
    const ref = doc(db, FirestoreCollections.admins, admin.id);

    // The grid keeps a version per change, read back from the panel's history button
    const previous = (await getDoc(ref)).data() as IAdmin | undefined;
    const sStoredSignature = outboundPricingSignature(previous?.telephony?.call_outbound_pricing);

    if (loaded && sStoredSignature !== outboundPricingSignature(loaded.telephony?.call_outbound_pricing)) {
      const [latest] = await getOutboundPricingHistory(admin.id);
      const sBy = latest ? ` par ${latest.author || 'inconnu'} le ${new Date(latest.created).toLocaleString('fr-FR')}` : '';
      throw new UpdateAdminFailure(`La grille tarifaire a été modifiée${sBy} depuis l'ouverture de cette page : rechargez-la avant d'enregistrer`, 'outbound-pricing-conflict');
    }

    const grid = admin.telephony?.call_outbound_pricing;
    const bGridChanged = !!grid && outboundPricingSignature(grid) !== sStoredSignature;

    await setDoc(ref, data);

    // The config is saved whatever happens to its history: a lost version must not read as a failed save
    if (bGridChanged) {
      try {
        // `version` is the id each call priced by this grid carries in `billing.grid_version`
        await setDoc(doc(collection(ref, ADMIN_HISTORY_SUBCOLLECTION)), { field: OUTBOUND_PRICING_HISTORY_FIELD, value: grid, version: outboundPricingVersion(grid), author: auth.email, created: Date.now() });
      } catch (e) {
        console.error('outbound pricing history not written: ', e);
      }
    }

    return;
  } catch (e) {
    if (e instanceof UpdateAdminFailure) throw e;
    console.log("error: ", e);
    throw new UpdateAdminFailure();
  }
}

export class UpdateAdminFailure extends Failure {
  constructor(message = "Unknown error", code = "unknown-error") {
    super(message, code);
  }
}

export default updateAdmin;
